import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import './ForgotPassword.css'; // We'll create this file too

/**
 * Completely rewritten ForgotPassword component with simplified styling approach
 * - Uses plain CSS instead of styled-components
 * - Avoids theme dependencies that could cause rendering issues
 * - Uses regular HTML form elements with validation
 */
const ForgotPassword = () => {
  const { requestPasswordReset } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
    if (error) setError(null);
  };

  const validateEmail = () => {
    if (!email) {
      setEmailError(t('auth.login.emailRequired'));
      return false;
    }
    
    // Simple email validation
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous messages
    setSuccessMessage('');
    setError(null);
    
    // Validate the email
    if (!validateEmail()) return;
    
    setIsSubmitting(true);
    
    try {
      await requestPasswordReset(email.trim());
      setSuccessMessage(t('auth.forgot.sent'));
      setEmail(''); // Clear the form
    } catch (err) {
      setError(err.message || 'Failed to send reset instructions. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <h1 className="forgot-password-title">{t('auth.forgot.title')}</h1>
      <p>{t('auth.access.privilegedPassword')}</p>
      <p>{t('auth.access.passwordMoved')} <Link to="/login">{t('auth.access.send')}</Link></p>
      
      {successMessage && (
        <div className="alert-box success" role="status">
          {successMessage}
        </div>
      )}
      
      {error && (
        <div className="alert-box error" role="alert">
          {error}
        </div>
      )}
      
      <form className="forgot-password-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email" className="form-label">{t('auth.forgot.email')}</label>
          <input
            type="email"
            id="email"
            className={`form-input ${emailError ? 'input-error' : ''}`}
            value={email}
            onChange={handleChange}
            placeholder="Enter your email"
            autoComplete="email"
          />
          {emailError && <div className="error-text">{emailError}</div>}
        </div>
        
        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('auth.forgot.sending') : t('auth.forgot.sendLink')}
        </button>
      </form>
      
      <Link to="/adminlogin" className="back-to-login-link">
        {t('auth.access.adminLogin')}
      </Link>
    </div>
  );
};

export default ForgotPassword;
